export type TimestampedValue = { timestamp: number; value: number };

/**
 * `series − baseline`, joined on TIMESTAMP with a forward fill.
 *
 * Subtracting index-by-index looks equivalent and is not. The portfolio chart's two
 * profit-only series come from separate aggregations that are each resampled into 2-day
 * buckets, and the buckets do not always land on the same instant: the candle route's pair is
 * perfectly aligned (256 points, identical timestamps) while the fast route's disagrees on 2 of
 * 243 — same days, a few hours apart. An index join therefore subtracts values from different
 * moments at exactly the points where the two series were sampled differently, and reports a
 * plausible number while doing it.
 *
 * Forward fill rather than an exact match: an exact join drops any point the baseline has no
 * row for, which on the measured data loses 2 points from the middle of the line. The most
 * recent baseline value at or before a point is what that point's baseline actually was.
 *
 * A point earlier than every baseline point has no baseline to stand on and is dropped — there
 * is nothing to subtract, and treating the baseline as 0 there would draw the series' own value
 * as if it were a difference.
 *
 * Both inputs must be sorted ascending by timestamp; the server returns them that way.
 * Mirrors data-service's `subtractBaselineForwardFilled`.
 */
export const subtractBaselineForwardFilled = (
  series: TimestampedValue[],
  baseline: TimestampedValue[],
): TimestampedValue[] => {
  if (!series?.length || !baseline?.length) {
    return [];
  }

  const result: TimestampedValue[] = [];
  let cursor = -1;

  for (const point of series) {
    while (
      cursor + 1 < baseline.length &&
      baseline[cursor + 1].timestamp <= point.timestamp
    ) {
      cursor += 1;
    }

    if (cursor < 0) {
      continue;
    }

    result.push({
      timestamp: point.timestamp,
      value: point.value - baseline[cursor].value,
    });
  }

  return result;
};
